/**
 * The member-facing WebSocket upgrade (TRD section 7).
 *
 *   GET /v1/connect   WebSocket upgrade, RelayUpgradeQuery + RelayUpgradeProtocols
 *
 * Every client, Desktop and Web alike, authenticates the same way: it offers
 * two WebSocket subprotocols, the protocol version and its Keycloak access
 * token, and the Worker answers by selecting the version alone.
 *
 *   Sec-WebSocket-Protocol: healthos.relay.v1, bearer.<access token>   (client)
 *   Sec-WebSocket-Protocol: healthos.relay.v1                          (Worker)
 *
 * Browsers cannot set an `Authorization` header on a WebSocket upgrade, but
 * they can offer subprotocols (`new WebSocket(url, relayUpgradeProtocols(t))`),
 * so this is the one place both surfaces can carry a bearer. It keeps the
 * token out of the URL, where access logs and browser history would keep it.
 * A compact JWS is all base64url and dots, which are legal in a subprotocol
 * token. The Worker never echoes the bearer entry and strips the header before
 * the upgrade reaches the member's inbox.
 *
 * The token is a Keycloak access token from the realm the Worker trusts. The
 * Worker checks its signature and issuer, that it carries `user:healthos`
 * (`RELAY_MEMBER_ROLE`, in `realm_access.roles` or `thriveRoles`), and that
 * `companyId` and `subscriptionId` are present as UUIDs. It checks no
 * audience and no requesting client. Anything else a member may receive is
 * the publisher's decision.
 *
 * There is no long-poll route: when a socket cannot be established the client
 * polls the producer's undelivered fetch on core-api instead
 * (`undelivered.ts`), so the Worker has exactly one member-facing route. There
 * are no cursors in the query because the Relay keeps no log to resume from,
 * and no topics because there is no subscription: every socket gets everything.
 */
import { z } from "zod";
import { RelaySurface } from "./topics.js";
/**
 * The upgrade names only the surface. There is no topic list and no
 * capability: the inbox sends every event published for the member to every
 * one of their live sockets, and a client ignores (but still acknowledges)
 * topics it does not handle. `client` is kept for the ack bookkeeping
 * (`RelayPublishResponse.acked` is per surface) and for the undelivered rows,
 * which are keyed by surface.
 */
export const RelayUpgradeQuery = z.strictObject({ client: RelaySurface });
/** The realm role every member token must carry to open a socket. */
export const RELAY_MEMBER_ROLE = "user:healthos";
/** The one subprotocol the Worker speaks, and the only value it ever selects. */
export const RELAY_PROTOCOL = "healthos.relay.v1";
/** Prefix of the subprotocol entry that carries the member's access token. */
export const RELAY_BEARER_PROTOCOL_PREFIX = "bearer.";
/**
 * The bearer as it may appear inside a subprotocol entry: base64url and dots,
 * which covers a compact JWS and the Worker's local-only `dev.<uuid>` bearer.
 * Whether it is a valid token is the Worker's call, after parsing.
 */
export const RelayBearerToken = z.string().regex(/^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*$/);
/** What a client passes as the second argument to `new WebSocket(url, protocols)`. */
export function relayUpgradeProtocols(accessToken) {
    return [RELAY_PROTOCOL, `${RELAY_BEARER_PROTOCOL_PREFIX}${accessToken}`];
}
/**
 * The upgrade's `Sec-WebSocket-Protocol` header, parsed to the token it
 * carries. Exactly two entries: the protocol version and one bearer. A missing
 * version means a client this Worker does not speak to; a second bearer or an
 * unknown entry is refused rather than guessed at.
 */
export const RelayUpgradeProtocols = z
    .string()
    .transform((raw) => raw
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0))
    .superRefine((entries, ctx) => {
    if (entries.filter((entry) => entry === RELAY_PROTOCOL).length !== 1) {
        ctx.addIssue({ code: "custom", message: `offer ${RELAY_PROTOCOL} exactly once` });
    }
    const bearers = entries.filter((entry) => entry.startsWith(RELAY_BEARER_PROTOCOL_PREFIX));
    if (bearers.length !== 1) {
        ctx.addIssue({ code: "custom", message: "offer exactly one bearer" });
    }
    if (entries.length !== 2) {
        ctx.addIssue({ code: "custom", message: "offer only the version and the bearer" });
    }
})
    .transform((entries) => ({
    token: (entries.find((entry) => entry.startsWith(RELAY_BEARER_PROTOCOL_PREFIX)) ?? "").slice(RELAY_BEARER_PROTOCOL_PREFIX.length),
}))
    .pipe(z.strictObject({ token: RelayBearerToken }));
//# sourceMappingURL=transport.js.map